/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import Swal from "sweetalert2";

import { getNotificationsAction, readNotificationsAction, clearAllNotificationsAction } from "../../../store/actions/notification/NotificationActions";

const Notification = ({ onNote }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const { notifications } = useSelector((state) => state.notification);
  const notificationsRows = notifications?.rows || [];
  const totalPages = notifications?.pages || 1;
  // Local unread count for immediate UI feedback, fallback to notifications.unreadCount
  const [localUnreadCount, setLocalUnreadCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  // Debounce for scroll event
  const debounceRef = useRef(false);
  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container || currentPage >= totalPages || isLoading || debounceRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isBottom && !isLoading && currentPage < totalPages) {
      debounceRef.current = true;
      setIsLoading(true);
      setCurrentPage((prev) => prev + 1);
      setTimeout(() => {
        debounceRef.current = false;
      }, 500); // 500ms debounce
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadNotifications = async () => {
      try {
        await dispatch(getNotificationsAction(currentPage, pageSize));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    if (isLoading) {
      loadNotifications();
    }
    return () => { cancelled = true; };
  }, [isLoading, currentPage, pageSize, dispatch]);

  // Reset localUnreadCount when notifications.unreadCount changes
  useEffect(() => {
    setLocalUnreadCount(null);
  }, [notifications?.unreadCount]);
  const [readIds, setReadIds] = useState([]);
  const handleNotificationClick = (note) => {
    dispatch(readNotificationsAction([note._id]));
    setReadIds((prev) => [...prev, note._id]);
    if (!note.read && !readIds.includes(note._id)) {
      setLocalUnreadCount((prev) => {
        // If prev is null, start from notifications.unreadCount
        const base = prev === null ? (notifications?.unreadCount || 0) : prev;
        return base > 0 ? base - 1 : 0;
      });
    }
      if (note.entityType === 'property-reported' && note.entityId) {
        navigate(`reported-property-list?id=${note.entityId}`);
      } else if(note.entityType === 'property' && note.entityId) {
        navigate(`property-details/${note.entityId}`);
      } else if(note.entityType === 'property-verification'  && note.entityId) {
        navigate(`pending-verification-list?id=${note.entityId}`);
      } else if(note.entityType === 'property-verification-message'  && note.entityId) {
        navigate(`verification-list?id=${note.entityId}`);
      } else if(note.entityType === 'bank-verification'  && note.entityId) {
        navigate(`pending-bank-verification-list?id=${note.entityId}`);
      } else if(note.entityType === 'bank-verification-message'  && note.entityId) {
        navigate(`bank-verification-list?id=${note.entityId}`);
      }
  };

  // eslint-disable-next-line no-unused-vars
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const handleDropdownToggle = useCallback((isOpen) => {
    setDropdownOpen(isOpen);
    if (isOpen) {
      setCurrentPage(1);
      setIsLoading(true);
      // Clear notification list
      dispatch({ type: 'CONFIRMED_GET_NOTIFICATIONS', payload: { rows: [], pages: 1, unreadCount: 0 } });
    }
  }, [dispatch]);

  const handleClearAllNotifications = async () => {
    const result = await Swal.fire({
      title: "Clear All Notifications?",
      text: "This action will permanently delete all notifications and cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, clear all!",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(clearAllNotificationsAction());
        if (response.data.success) {
          Swal.fire({
            title: "Cleared!",
            text: "All notifications have been permanently cleared.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false
          });
          // Update local state immediately
          setLocalUnreadCount(0);
        } else {
          Swal.fire({
            title: "Error!",
            text: response.data?.msg || "Failed to clear notifications.",
            icon: "error"
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error!",
          text: "An unexpected error occurred while clearing notifications.",
          icon: "error"
        });
      }
    }
  };

  return (
    <Dropdown className="nav-item dropdown notification_dropdown" onToggle={handleDropdownToggle}>
      <Dropdown.Toggle variant="" className="nav-link  ai-icon i-false">
        <svg
          width={26}
          height={26}
          viewBox="0 0 26 26"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21.75 14.8385V12.0463C21.7471 9.88552 20.9385 7.80353 19.4821 6.20735C18.0258 4.61116 16.0264 3.61555 13.875 3.41516V1.625C13.875 1.39294 13.7828 1.17038 13.6187 1.00628C13.4546 0.842187 13.2321 0.75 13 0.75C12.7679 0.75 12.5454 0.842187 12.3813 1.00628C12.2172 1.17038 12.125 1.39294 12.125 1.625V3.41534C9.97361 3.61572 7.97429 4.61131 6.51794 6.20746C5.06159 7.80361 4.25291 9.88555 4.25 12.0463V14.8383C3.26257 15.0412 2.37529 15.5784 1.73774 16.3593C1.10019 17.1401 0.751339 18.1169 0.75 19.125C0.750764 19.821 1.02757 20.4882 1.51969 20.9803C2.01181 21.4724 2.67904 21.7492 3.375 21.75H8.71346C8.91521 22.738 9.45205 23.6259 10.2331 24.2636C11.0142 24.9013 11.9916 25.2497 13 25.2497C14.0084 25.2497 14.9858 24.9013 15.7669 24.2636C16.548 23.6259 17.0848 22.738 17.2865 21.75H22.625C23.321 21.7492 23.9882 21.4724 24.4803 20.9803C24.9724 20.4882 25.2492 19.821 25.25 19.125C25.2486 18.117 24.8998 17.1402 24.2622 16.3594C23.6247 15.5786 22.7374 15.0414 21.75 14.8385ZM6 12.0463C6.00232 10.2113 6.73226 8.45223 8.02974 7.15474C9.32723 5.85726 11.0863 5.12732 12.9212 5.125H13.0788C14.9137 5.12732 16.6728 5.85726 17.9703 7.15474C19.2677 8.45223 19.9977 10.2113 20 12.0463V14.75H6V12.0463ZM13 23.5C12.4589 23.4983 11.9316 23.3292 11.4905 23.0159C11.0493 22.7026 10.716 22.2604 10.5363 21.75H15.4637C15.284 22.2604 14.9507 22.7026 14.5095 23.0159C14.0684 23.3292 13.5411 23.4983 13 23.5ZM22.625 20H3.375C3.14298 19.9999 2.9205 19.9076 2.75644 19.7436C2.59237 19.5795 2.50014 19.357 2.5 19.125C2.50076 18.429 2.77757 17.7618 3.26969 17.2697C3.76181 16.7776 4.42904 16.5008 5.125 16.5H20.875C21.571 16.5008 22.2382 16.7776 22.7303 17.2697C23.2224 17.7618 23.4992 18.429 23.5 19.125C23.4999 19.357 23.4076 19.5795 23.2436 19.7436C23.0795 19.9076 22.857 19.9999 22.625 20Z"
            fill="#3B4CB8"
          />
        </svg>
        <div className="pulse-css" > {localUnreadCount === null ? (notifications?.unreadCount || 0) : localUnreadCount} </div>
      </Dropdown.Toggle>

      <Dropdown.Menu align="end" className="mt-2">
        <div 
            className="widget-media dz-scroll p-3 height380"
            ref={scrollRef}
            onScroll={handleScroll}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Notifications</h4>
            {notificationsRows.length > 0 && (
              <button 
                className="btn btn-sm btn-outline-danger"
                onClick={handleClearAllNotifications}
                title="Clear all notifications permanently"
                style={{ fontSize: '12px', padding: '2px 8px' }}
              >
                <i className="fa fa-trash me-1"></i>
                Clear All
              </button>
            )}
          </div>
          <ul className="timeline">
            {notificationsRows.length === 0 && (
              <li style={{ border: 'none', background: 'none' }}>
                <div className="text-center py-4">
                  <div className="mb-3">
                    <i 
                      className="fa fa-bell-slash" 
                      style={{ 
                        fontSize: '48px', 
                        color: '#dee2e6',
                        opacity: 0.7
                      }}
                    />
                  </div>
                  <h6 className="mb-2 text-muted" style={{ fontWeight: '500' }}>
                    No notifications yet
                  </h6>
                  <p className="mb-0 small text-muted" style={{ fontSize: '13px', opacity: 0.8 }}>
                    You'll see notifications here when they arrive
                  </p>
                </div>
              </li>
            )}

            {notificationsRows.map((note) => (
                <li
                  key={note._id}
                  onClick={() => handleNotificationClick(note)}
                  style={{
                    cursor: 'pointer',
                    background: note.read || readIds.includes(note._id) ? '#f8f9fa' : '#eaf1ff',
                    fontWeight: note.read || readIds.includes(note._id) ? 'normal' : 'bold',
                    opacity: note.read || readIds.includes(note._id) ? 0.7 : 1
                  }}
                >
                <div className="timeline-panel" style={{ padding: '10px 7px'}}>
                  <div
                    className={`media me-2 ${
                      note.entityType === "property"
                        ? "media-info"
                        : note.entityType === "property-reported"
                        ? "media-danger"
                        : "media-primary"
                    }`}
                  >
                    <i
                      className={`fa ${
                        note.entityType === "property"
                          ? "fa-bell"
                          : note.entityType === "property-reported"
                          ? "fa-exclamation-triangle"
                          : "fa-bell"
                      }`}
                    />
                  </div>
                  <div className="media-body">
                    <h6 className="mb-1">{note.title}</h6>
                    <small className="d-block">
                      {new Date(note.createdAt).toLocaleString()}
                    </small>
                    <p className="mb-0 text-muted small">{note.message}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="ps__rail-x" style={{ left: 0, bottom: 0 }}>
            <div
              className="ps__thumb-x"
              tabIndex={0}
              style={{ left: 0, width: 0 }}
            />
          </div>
          <div className="ps__rail-y" style={{ top: 0, right: 0 }}>
            <div
              className="ps__thumb-y"
              tabIndex={0}
              style={{ top: 0, height: 0 }}
            />
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Notification;
